import { useCallback, useEffect, useReducer, useRef } from 'react';
import { api } from '@/lib/api';
import type { GeofenceOverlay, SiteMarker } from '@/components/dashboard/DroneMap';
import type { MaintenancePrediction } from '../compliance/_components/complianceShared';
import type { ActiveMission } from './types';

interface ContextDataState {
  geofences: GeofenceOverlay[];
  sites: SiteMarker[];
  activeMissions: ActiveMission[];
  /** Every drone's prediction rows. This request was already being made for the
   *  header KPI and its answer thrown away; keeping it costs no extra call. */
  maintenancePredictions: MaintenancePrediction[];
  missionsCountFailed: boolean;
  maintenanceCountFailed: boolean;
  geofencesFailed: boolean;
  sitesFailed: boolean;
}

type ContextDataAction =
  | { type: 'MISSIONS_OK'; payload: ActiveMission[] }
  | { type: 'MISSIONS_FAIL' }
  | { type: 'MAINTENANCE_OK'; payload: MaintenancePrediction[] }
  | { type: 'MAINTENANCE_FAIL' }
  | { type: 'GEOFENCES_OK'; payload: GeofenceOverlay[] }
  | { type: 'GEOFENCES_FAIL' }
  | { type: 'SITES_OK'; payload: SiteMarker[] }
  | { type: 'SITES_FAIL' };

const initialContextData: ContextDataState = {
  geofences: [],
  sites: [],
  activeMissions: [],
  maintenancePredictions: [],
  missionsCountFailed: false,
  maintenanceCountFailed: false,
  geofencesFailed: false,
  sitesFailed: false,
};

function contextDataReducer(state: ContextDataState, action: ContextDataAction): ContextDataState {
  switch (action.type) {
    case 'MISSIONS_OK': return { ...state, activeMissions: action.payload, missionsCountFailed: false };
    case 'MISSIONS_FAIL': return { ...state, missionsCountFailed: true };
    case 'MAINTENANCE_OK':
      return { ...state, maintenancePredictions: action.payload, maintenanceCountFailed: false };
    case 'MAINTENANCE_FAIL': return { ...state, maintenanceCountFailed: true };
    case 'GEOFENCES_OK': return { ...state, geofences: action.payload, geofencesFailed: false };
    case 'GEOFENCES_FAIL': return { ...state, geofencesFailed: true };
    case 'SITES_OK': return { ...state, sites: action.payload, sitesFailed: false };
    case 'SITES_FAIL': return { ...state, sitesFailed: true };
    default: return state;
  }
}

interface GeofenceApiRow {
  id: string;
  name: string;
  type: string;
  geometry: GeoJSON.Geometry;
  color: string;
  site_id?: string | null;
  is_active?: boolean;
}

/** Mission refresh cadence, matching the alarm list's poll in useCopAlerts. */
const MISSION_REFRESH_MS = 30_000;
/** Comfortably under the refresh interval, so a hung request cannot still be
 *  outstanding when the next one is issued. */
const MISSION_REQUEST_TIMEOUT_MS = 10_000;

interface SiteApiRow {
  id: string;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  is_active: boolean;
}

/** Loads COP context overlays (missions, maintenance health, geofences, sites). */
export function useCopContextData() {
  const [state, dispatch] = useReducer(contextDataReducer, initialContextData);
  const missionRefreshRef = useRef<ReturnType<typeof setInterval>>(undefined);
  /** Issue order for mission loads, so a slow response cannot overwrite a newer one. */
  const missionSeqRef = useRef(0);

  const loadGeofences = useCallback(() => {
    api
      .get<{ data: GeofenceApiRow[] }>('/api/geofences')
      .then((r) => {
        dispatch({
          type: 'GEOFENCES_OK',
          payload: r.data.map((g) => ({
            id: g.id,
            name: g.name,
            site_id: g.site_id ?? null,
            type: g.type as GeofenceOverlay['type'],
            geometry: g.geometry,
            color: g.color,
            is_active: g.is_active,
          })),
        });
      })
      .catch(() => {
        console.warn('Failed to load geofences for map overlay');
        dispatch({ type: 'GEOFENCES_FAIL' });
      });
  }, []);

  /**
   * The active-mission set is NOT a load-once snapshot: it changes under the
   * operator while the screen stays open. A launch adds a mission (the COP calls
   * this directly off the dispatch response), and a landing removes one — the
   * backend flips the mission to `completed` when the flight closes, which no
   * client action triggers and no WebSocket channel announces. Left unrefreshed,
   * the panel claimed "No mission assigned" for the whole of a live sortie and
   * kept showing a mission after the aircraft was back on the tray.
   */
  const loadMissions = useCallback(() => {
    // Only the newest request may write. Responses are not ordered: a poll issued
    // on a stalled connection can land AFTER the refresh a launch triggered, and
    // an unguarded dispatch would then restore the pre-launch snapshot over the
    // live answer — blanking the panel for an airborne aircraft, which is the
    // exact defect this hook exists to fix, reintroduced by its own backstop.
    const seq = ++missionSeqRef.current;
    api
      .get<{ data: ActiveMission[] }>('/api/missions/active', {
        // api.ts: "polling hooks must never leave a request hanging forever".
        // Without this the stale-response window above is unbounded.
        signal: AbortSignal.timeout(MISSION_REQUEST_TIMEOUT_MS),
      })
      .then((r) => {
        if (seq !== missionSeqRef.current) return;
        dispatch({ type: 'MISSIONS_OK', payload: r.data });
      })
      .catch(() => {
        if (seq !== missionSeqRef.current) return;
        console.warn('Failed to load active missions for COP');
        dispatch({ type: 'MISSIONS_FAIL' });
      });
  }, []);

  // Same cadence the alarm list already polls at (see useCopAlerts). It is the
  // backstop, not the primary path: a launch from this screen refreshes
  // immediately and so does a landing, so the interval exists to catch what this
  // browser did not do — a sortie launched by another operator, or a mission the
  // backend ended for a reason no client saw.
  useEffect(() => {
    loadMissions();
    missionRefreshRef.current = setInterval(loadMissions, MISSION_REFRESH_MS);
    return () => clearInterval(missionRefreshRef.current);
  }, [loadMissions]);

  useEffect(() => {
    api
      .get<{ predictions: MaintenancePrediction[] }>('/api/maintenance/predictions')
      .then((r) => dispatch({ type: 'MAINTENANCE_OK', payload: r.predictions ?? [] }))
      .catch(() => {
        console.warn('Failed to load maintenance predictions for COP KPI');
        dispatch({ type: 'MAINTENANCE_FAIL' });
      });

    loadGeofences();

    api.get<{ data: SiteApiRow[] }>('/api/sites')
      .then((r) => dispatch({ type: 'SITES_OK', payload: r.data }))
      .catch(() => {
        console.warn('Failed to load sites for map overlay');
        dispatch({ type: 'SITES_FAIL' });
      });
  }, [loadGeofences]);

  return { ...state, loadGeofences, loadMissions };
}
